import { useState, useEffect } from "react"
import {
  ArrowLeft,
  User,
  XCircle,
  FileText,
  AlertCircle,
  Loader,
  Code,
  Check,
  CheckCircle2,
  Clock,
} from "lucide-react"
import {
  Radar,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  ResponsiveContainer,
} from "recharts"
import { useUser } from "../contexts/UserContext"
import NotesViewer from "./NotesViewer"
import { useAuth } from "../contexts/AuthProvider"
import toast from "react-hot-toast"
import InterviewerFeedbackModal from "./InterviewerFeedbackModal"
import { updateCandidateStatus, saveInterviewerFeedback } from "../services/interviewService"
import VideoPlayer from "../components/VideoPlayer"
import InterviewDetailsShimmer from "./InterviewDetailsShimmer"

// ── Constants ─────────────────────────────────────────────────────────────────
const DM   = "'DM Sans', sans-serif"
const MONO = "'IBM Plex Mono', monospace"

// ── Score helpers ─────────────────────────────────────────────────────────────
const scoreColor = (s) => {
  const n = parseFloat(s)
  if (n >= 7) return "#16a34a"
  if (n >= 5) return "#d97706"
  return "#dc2626"
}
const scoreLabel = (s) => {
  const n = parseFloat(s)
  if (n >= 7) return "Good"
  if (n >= 5) return "Average"
  return "Poor"
}
const scoreBadgeStyle = (s) => {
  const n = parseFloat(s)
  if (n >= 7) return { background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#15803d" }
  if (n >= 5) return { background: "#fffbeb", border: "1px solid #fde68a", color: "#b45309" }
  return { background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626" }
}

// ── Primitives ────────────────────────────────────────────────────────────────
const SectionLabel = ({ children }) => (
  <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 mb-3" style={{ fontFamily: DM }}>
    {children}
  </p>
)

const Badge = ({ children, style }) => (
  <span
    className="inline-block text-[11px] font-bold uppercase tracking-widest px-2 py-0.5"
    style={{ borderRadius: 0, ...style }}
  >
    {children}
  </span>
)

const ScoreBar = ({ score, max = 10 }) => {
  const pct = (parseFloat(score) / max) * 100
  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-1.5 bg-zinc-100">
        <div style={{ width: `${pct}%`, background: scoreColor(score), height: "100%" }} />
      </div>
      <span
        className="text-sm font-bold shrink-0"
        style={{ fontFamily: MONO, color: scoreColor(score), minWidth: 36 }}
      >
        {score}/10
      </span>
    </div>
  )
}

const DotList = ({ items, color }) => (
  <div className="space-y-2">
    {items.map((p, i) => (
      <div key={i} className="flex items-start gap-2.5">
        <div className="w-1 h-1 shrink-0 mt-1.5" style={{ background: color }} />
        <p className="text-[15px] text-zinc-700 leading-relaxed" style={{ fontFamily: DM }}>{p}</p>
      </div>
    ))}
  </div>
)

// ── Transcript bubble ─────────────────────────────────────────────────────────
const Bubble = ({ role, text }) => {
  const isAI = role === "assistant"
  return (
    <div className={`flex ${isAI ? "justify-start" : "justify-end"} mb-3`}>
      {isAI && (
        <div
          className="w-5 h-5 border border-zinc-200 flex items-center justify-center shrink-0 mr-2.5 mt-0.5 bg-zinc-50"
          style={{ borderRadius: 0 }}
        >
          <span className="text-[9px] font-bold text-zinc-400" style={{ fontFamily: MONO }}>AI</span>
        </div>
      )}
      <div
        className="max-w-[80%] px-4 py-3 text-[15px] leading-relaxed border"
        style={{
          borderRadius: 0,
          ...(isAI
            ? { background: "#ffffff", borderColor: "#e4e4e7", color: "#3f3f46" }
            : { background: "#eef2ff", borderColor: "#c7d2fe", color: "#3730a3" }),
        }}
      >
        {text}
      </div>
      {!isAI && (
        <div
          className="w-5 h-5 border flex items-center justify-center shrink-0 ml-2.5 mt-0.5"
          style={{ borderRadius: 0, background: "#eef2ff", borderColor: "#c7d2fe" }}
        >
          <span className="text-[9px] font-bold" style={{ fontFamily: MONO, color: "#4f46e5" }}>C</span>
        </div>
      )}
    </div>
  )
}

// ── Main ──────────────────────────────────────────────────────────────────────
const InterviewDetailsPage = ({ onBack, interview, onRefreshInterviews, interviewers }) => {
  const { user } = useAuth()
  const { organizationId, isAdmin } = useUser()

  const [interviewData, setInterviewData]     = useState(null)
  const [loading, setLoading]                 = useState(true)
  const [error, setError]                     = useState(null)
  const [notConducted, setNotConducted]       = useState(false)
  const [reportGenerating, setReportGenerating] = useState(false)
  const [retryTrigger, setRetryTrigger]       = useState(0)

  const [showNotesViewer, setShowNotesViewer] = useState(false)
  const [showFeedbackModal, setShowFeedbackModal] = useState(false)

  const [interviewerFeedback, setInterviewerFeedback] = useState("")
  const [hiringDecision, setHiringDecision]   = useState(null)
  const [isSavingFeedback, setIsSavingFeedback] = useState(false)
  const [feedbackSaved, setFeedbackSaved]     = useState(false)

  const [isUpdatingHired, setIsUpdatingHired]       = useState(false)
  const [isUpdatingRejected, setIsUpdatingRejected] = useState(false)

  // Page-level tabs
  const [mainTab, setMainTab]   = useState("report")
  const [activeTab, setActiveTab] = useState("feedback")

  const meetingId = interview?.id

  useEffect(() => {
    if (!meetingId) { setError("No interview ID provided."); setLoading(false); return }
    setInterviewData(null)
    setError(null)
    setNotConducted(false)
    setReportGenerating(false)
    setLoading(true)

    const fetchReport = async () => {
      try {
        const res = await fetch(`https://hire100x-e1dd4.uc.r.appspot.com/reports?meetingId=${meetingId}`)
        if (res.status === 400) { setNotConducted(true); setLoading(false); return }
        if (res.status === 202) { setReportGenerating(true); setLoading(false); return }
        const json = await res.json()
        if (json.success && json.data) {
          const data = { ...json.data, transcripts: json.transcripts }
          setInterviewData(data)
          if (data.interviewerFeedback) setInterviewerFeedback(data.interviewerFeedback)
          if (data.hiringDecision) setHiringDecision(data.hiringDecision)
          if (data.interviewerFeedback || data.hiringDecision) setFeedbackSaved(true)
          setLoading(false)
        } else {
          setError("The report could not be loaded. Please retry.")
          setLoading(false)
        }
      } catch (err) {
        console.error("Report fetch error:", err)
        setError("Failed to fetch interview report. Please retry.")
        setLoading(false)
      }
    }
    fetchReport()
  }, [meetingId, retryTrigger])

  // ── Handlers ─────────────────────────────────────────────────────────────────
  const handleSaveFeedback = async (decision, feedback) => {
    if (!organizationId || !meetingId) {
      toast.error("Missing required information", { icon: "⚠️", duration: 3000 })
      return
    }
    setIsSavingFeedback(true)
    try {
      const success = await saveInterviewerFeedback(meetingId, decision, feedback)
      if (success) {
        setHiringDecision(decision)
        setInterviewerFeedback(feedback)
        setFeedbackSaved(true)
        setShowFeedbackModal(false)
        toast.success("Feedback saved successfully", { icon: "✅", duration: 3000 })
        if (onRefreshInterviews) onRefreshInterviews()
      } else throw new Error("Failed to save feedback")
    } catch {
      toast.error("Failed to save feedback", { icon: "❌", duration: 3000 })
    } finally {
      setIsSavingFeedback(false)
    }
  }

  const handleStatusUpdate = async (status) => {
    if (!organizationId || !meetingId) {
      toast.error("Missing required information", { icon: "⚠️", duration: 3000 })
      return
    }
    if (status === "hired") setIsUpdatingHired(true)
    else setIsUpdatingRejected(true)
    try {
      const success = await updateCandidateStatus(organizationId, meetingId, status)
      if (success) {
        if (status === "hired") toast.success("Candidate marked as hired", { icon: "🎉", duration: 3000 })
        else toast.error("Candidate marked as rejected", { icon: "❌", duration: 3000 })
        if (onRefreshInterviews) onRefreshInterviews()
      } else throw new Error("Failed to update candidate status")
    } catch {
      toast.error("Failed to update candidate status", { icon: "🔐", duration: 3000 })
    } finally {
      setIsUpdatingHired(false)
      setIsUpdatingRejected(false)
    }
  }

  // ── Shared top bar ───────────────────────────────────────────────────────────
  const TopBar = ({ hasData }) => {
    const candidateName = interview?.candidateName || interview?.candidate || "Candidate"
    const positionTitle = interview?.positionTitle || interview?.jobTitle || "—"

    return (
      <div className="bg-white border-b border-zinc-200 shrink-0 z-20">
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
          .hide-scroll::-webkit-scrollbar { display: none; }
          .hide-scroll { -ms-overflow-style: none; scrollbar-width: none; }
        `}</style>

        {/* ── Main header row ── */}
        <div className="px-6 py-3 flex items-center justify-between">
          {/* Left */}
          <div className="flex items-center gap-4">
            <button
              onClick={onBack}
              className="flex items-center justify-center w-7 h-7 border border-zinc-200 hover:border-zinc-400 text-zinc-400 hover:text-zinc-700 transition-all"
              style={{ borderRadius: 0 }}
            >
              <ArrowLeft size={13} strokeWidth={2} />
            </button>

            <div className="flex items-center gap-3">
              <div
                className="w-7 h-7 bg-zinc-900 flex items-center justify-center flex-shrink-0"
                style={{ borderRadius: 0 }}
              >
                <User size={13} className="text-white" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-zinc-900" style={{ fontFamily: DM }}>
                  {interview?.title || interviewData?.title || "Interview Details"}
                </p>
                <p className="text-[13px] text-zinc-500 mt-0.5" style={{ fontFamily: DM }}>
                  {candidateName}
                  {positionTitle !== "—" && <span className="text-zinc-300 mx-1">·</span>}
                  {positionTitle !== "—" && positionTitle}
                </p>
              </div>
            </div>
          </div>

          {/* Right — action buttons */}
          <div className="flex items-center gap-2">
            <button
              className={`flex items-center gap-1.5 h-9 px-3 border text-[11px] font-bold uppercase tracking-widest transition-colors ${
                hasData
                  ? "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-400 hover:text-zinc-900"
                  : "bg-zinc-50 border-zinc-100 text-zinc-300 cursor-not-allowed"
              }`}
              style={{ borderRadius: 0, fontFamily: DM }}
              onClick={() => hasData && window.open(`/compiler?id=${meetingId}`, "_blank", "noopener,noreferrer")}
              disabled={!hasData}
            >
              <Code size={12} strokeWidth={2} />
              Code
            </button>

            <button
              className={`flex items-center gap-1.5 h-9 px-3 border text-[11px] font-bold uppercase tracking-widest transition-colors ${
                hasData
                  ? "bg-white border-zinc-200 text-zinc-600 hover:border-zinc-400 hover:text-zinc-900"
                  : "bg-zinc-50 border-zinc-100 text-zinc-300 cursor-not-allowed"
              }`}
              style={{ borderRadius: 0, fontFamily: DM }}
              onClick={() => hasData && setShowNotesViewer(true)}
              disabled={!hasData}
            >
              <FileText size={12} strokeWidth={2} />
              Notes
            </button>

            {/* Mark as Hired */}
            <button
              className={`flex items-center gap-1.5 h-9 px-3 border text-[11px] font-bold uppercase tracking-widest transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                hasData
                  ? "bg-white border-zinc-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-200"
                  : "bg-zinc-50 border-zinc-100 text-zinc-300 cursor-not-allowed"
              }`}
              style={{ borderRadius: 0, fontFamily: DM }}
              onClick={() => hasData && handleStatusUpdate("hired")}
              disabled={!hasData || isUpdatingHired || isUpdatingRejected}
            >
              {isUpdatingHired
                ? <Loader size={12} className="animate-spin" />
                : <Check size={12} strokeWidth={2.5} />
              }
              {isUpdatingHired ? "Saving…" : "Hire"}
            </button>

            {/* Mark as Rejected */}
            <button
              className={`flex items-center gap-1.5 h-9 px-3 border text-[11px] font-bold uppercase tracking-widest transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                hasData
                  ? "bg-white border-zinc-200 text-red-600 hover:bg-red-50 hover:border-red-200"
                  : "bg-zinc-50 border-zinc-100 text-zinc-300 cursor-not-allowed"
              }`}
              style={{ borderRadius: 0, fontFamily: DM }}
              onClick={() => hasData && handleStatusUpdate("rejected")}
              disabled={!hasData || isUpdatingHired || isUpdatingRejected}
            >
              {isUpdatingRejected
                ? <Loader size={12} className="animate-spin" />
                : <XCircle size={12} strokeWidth={2} />
              }
              {isUpdatingRejected ? "Saving…" : "Reject"}
            </button>
          </div>
        </div>

        {/* ── Page-level tabs ── */}
        <div className="flex px-6 border-t border-zinc-100">
          {[
            {
              key: "report",
              label: "Interview Report",
              icon: (
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 2H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V6L9 2z" />
                  <path d="M9 2v4h4M6 9h4M6 11h4M6 7h1" />
                </svg>
              ),
            },
            {
              key: "recording",
              label: "Interview Recording",
              icon: (
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="1" y="3" width="10" height="10" rx="1" />
                  <path d="M11 6l4-2v8l-4-2V6z" />
                </svg>
              ),
            },
          ].map(({ key, label, icon }) => (
            <button
              key={key}
              onClick={() => setMainTab(key)}
              className={`flex items-center gap-2 px-4 py-3 text-[11px] font-bold uppercase tracking-widest transition-all relative mr-1 ${
                mainTab === key ? "text-zinc-900" : "text-zinc-400 hover:text-zinc-600"
              }`}
              style={{ borderRadius: 0, fontFamily: DM }}
            >
              {icon}
              {label}
              {mainTab === key && (
                <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-zinc-900" />
              )}
            </button>
          ))}
        </div>
      </div>
    )
  }

  // ── Empty / not-conducted / generating states ────────────────────────────────
  const StateScreen = ({ icon, title, description, showRetry }) => (
    <div className="h-screen flex flex-col overflow-hidden bg-zinc-50" style={{ fontFamily: DM }}>
      <TopBar hasData={false} />
      <div className="flex-1 flex flex-col items-center justify-center gap-3">
        {icon}
        <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-500" style={{ fontFamily: MONO }}>
          {title}
        </p>
        <p className="text-[13px] text-zinc-400 max-w-xs text-center leading-relaxed" style={{ fontFamily: DM }}>
          {description}
        </p>
        {showRetry && (
          <button
            onClick={() => setRetryTrigger((p) => p + 1)}
            className="mt-2 h-9 px-5 bg-zinc-900 text-white text-[11px] font-bold uppercase tracking-widest hover:bg-zinc-700 transition-colors"
            style={{ borderRadius: 0, fontFamily: DM }}
          >
            Retry
          </button>
        )}
      </div>
    </div>
  )

  const CalendarIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d4d4d8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
      <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
    </svg>
  )
  const CircleAlertIcon = () => (
    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d4d4d8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
  )

  if (notConducted)    return <StateScreen icon={<CalendarIcon />} title="Interview Not Conducted" description="This interview hasn't been conducted yet. Check back once it has taken place." />
  if (reportGenerating) return <StateScreen icon={<CalendarIcon />} title="Report Generating" description="The report is being generated. Check back in a few moments." />
  if (loading)         return <InterviewDetailsShimmer onBack={onBack} />
  if (error)           return <StateScreen icon={<CircleAlertIcon />} title="Failed to Load Report" description={error} showRetry />
  if (!interviewData)  return <StateScreen icon={<FileText size={32} className="text-zinc-300" />} title="No Interview Data" description="No data was found for this interview." />

  // ── Derive data ───────────────────────────────────────────────────────────────
  const qaPairs      = interviewData?.qaPairs || []
  const transcripts  = interviewData?.transcripts || []
  const skillRatings = interviewData?.skill_by_skill_rating || []
  const radarData    = skillRatings.map((s) => ({ subject: s.name_of_the_skill_tested, value: parseFloat(s.rating), fullMark: 10 }))
  const overallSkillsAvg = skillRatings.length > 0
    ? skillRatings.reduce((acc, s) => acc + parseFloat(s.rating), 0) / skillRatings.length
    : 0

  const commScore          = parseFloat(interviewData?.communication_skills?.score || 0)
  const candidateRating    = interviewData?.candidate_rating
  const verdictPassed      = interviewData?.final_verdict === "True" || interviewData?.final_verdict === "Yes"
  const explanations       = interviewData?.explanations || {}
  const ratingJustification = interviewData?.rating_justification
  const candidate          = interviewData?.candidate
  const position           = interviewData?.position
  const date               = interviewData?.date || interview?.date
  const duration           = interviewData?.duration || interview?.duration
  const cheatingProbability = interviewData?.cheating_probability

  const hiringDecisionLabel = hiringDecision === "no_hire" ? "No Hire" : hiringDecision === "hire" ? "Strong Hire" : hiringDecision === "conditional_hire" ? "Conditional Hire" : null
  const hiringDecisionStyle = hiringDecision === "no_hire"
    ? { background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626" }
    : hiringDecision === "hire"
    ? { background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#15803d" }
    : { background: "#fffbeb", border: "1px solid #fde68a", color: "#b45309" }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="h-screen flex flex-col overflow-hidden bg-zinc-50" style={{ fontFamily: DM }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        .hide-scroll::-webkit-scrollbar { display: none; }
        .hide-scroll { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

      <TopBar hasData={true} />

      {/* ── Body ── */}
      {mainTab === "report" ? (
        <div className="flex flex-1 min-h-0 bg-zinc-100 gap-px">

          {/* ════ LEFT — scrollable analysis ════ */}
          <div className="flex-1 flex flex-col min-w-0 overflow-y-auto hide-scroll bg-zinc-50">

            {/* Candidate header card */}
            <div className="bg-zinc-50 px-10 py-5 shrink-0">
              <div className="bg-white border border-zinc-200 px-8 py-5 flex items-center justify-between gap-6">
                <div className="flex flex-col gap-2 min-w-0">
                  <div className="flex items-center gap-2.5 flex-wrap">
                    <h1 className="text-xl font-bold text-zinc-900" style={{ fontFamily: DM }}>
                      {candidate?.name || interview?.candidateName || interview?.candidate || "Candidate"}
                    </h1>
                    <Badge style={{ background: "#ede9fe", color: "#6d28d9", border: "1px solid #ddd6fe" }}>
                      {position || interview?.positionTitle || interview?.jobTitle || "Engineer"}
                    </Badge>
                    <Badge
                      style={verdictPassed
                        ? { background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0" }
                        : { background: "#fef2f2", color: "#dc2626", border: "1px solid #fecaca" }}
                    >
                      {verdictPassed ? "Passed" : "Not Selected"}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-4 text-[13px] text-zinc-400 flex-wrap" style={{ fontFamily: MONO }}>
                    <span>
                      Interviewed:{" "}
                      <span className="text-zinc-600">
                        {date ? new Date(date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—"}
                      </span>
                    </span>
                    <span className="text-zinc-200">|</span>
                    <span>Duration: <span className="text-zinc-600">{duration ?? "—"}</span></span>
                    <span className="text-zinc-200">|</span>
                    <span>
                      Questions:{" "}
                      <span className="text-zinc-600">
                        {interviewData?.total_non_logistic_questions_answered ?? "—"}/{interviewData?.total_non_logistic_questions ?? "—"}
                      </span>
                    </span>
                  </div>
                </div>
                {candidateRating && (
                  <div className="shrink-0 text-right px-6 py-2 border-l border-zinc-100">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 mb-1" style={{ fontFamily: DM }}>Score</p>
                    <p className="text-4xl font-bold leading-none" style={{ fontFamily: MONO, color: scoreColor(candidateRating) }}>
                      {candidateRating}
                    </p>
                    <p className="text-[11px] text-zinc-400 mt-1" style={{ fontFamily: DM }}>/10</p>
                  </div>
                )}
              </div>
            </div>

            {/* Stat strip */}
            {(commScore > 0 || overallSkillsAvg > 0 || cheatingProbability != null) && (
              <div className="shrink-0 bg-zinc-50 px-10 pb-5">
                <div className="grid grid-cols-4 bg-white border border-zinc-200">
                  {[
                    { label: "Communication", value: `${commScore}/10`, sub: scoreLabel(commScore) },
                    { label: "Skills Avg",     value: `${overallSkillsAvg.toFixed(1)}/10`, sub: scoreLabel(overallSkillsAvg) },
                    { label: "CEFR Level",     value: "B1",  sub: "Intermediate" },
                    { label: "Cheating Risk",  value: `${cheatingProbability ?? "—"}%`, sub: "Low risk" },
                  ].map((s, i, arr) => (
                    <div key={s.label} className={`px-6 py-4 ${i < arr.length - 1 ? "border-r border-zinc-200" : ""}`}>
                      <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 mb-1" style={{ fontFamily: DM }}>{s.label}</p>
                      <p className="text-xl font-bold text-zinc-900" style={{ fontFamily: MONO }}>{s.value}</p>
                      <p className="text-[15px] text-zinc-400 mt-0.5" style={{ fontFamily: DM }}>{s.sub}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Content sections */}
            <div className="px-10 py-7 space-y-4">

              {/* Interview Analysis */}
              {explanations.candidate_positives && (
                <div className="bg-white border border-zinc-200">
                  <div className="px-6 py-3.5 border-b border-zinc-200">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400" style={{ fontFamily: DM }}>Interview Analysis</p>
                  </div>
                  <div className="px-6 py-5 grid grid-cols-3 gap-6 divide-x divide-zinc-100">
                    <div>
                      <SectionLabel>Strengths</SectionLabel>
                      <DotList items={explanations.candidate_positives || []} color="#34d399" />
                    </div>
                    <div className="pl-6">
                      <SectionLabel>Weaknesses</SectionLabel>
                      <DotList items={explanations.candidate_negatives || []} color="#f87171" />
                    </div>
                    <div className="pl-6">
                      <SectionLabel>Areas of Improvement</SectionLabel>
                      <DotList items={explanations.areas_of_improvement || []} color="#fbbf24" />
                    </div>
                  </div>
                  {ratingJustification && (
                    <div className="px-6 pb-5 pt-4 border-t border-zinc-100">
                      <SectionLabel>Overall Assessment</SectionLabel>
                      <p className="text-[15px] text-zinc-500 leading-relaxed" style={{ fontFamily: DM }}>{ratingJustification}</p>
                    </div>
                  )}
                </div>
              )}

              {/* AI Summary fallback */}
              {!explanations.candidate_positives && interviewData?.aiSummary && (
                <div className="bg-white border border-zinc-200">
                  <div className="px-6 py-3.5 border-b border-zinc-200">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400" style={{ fontFamily: DM }}>AI Summary</p>
                  </div>
                  <div className="px-6 py-5">
                    <p className="text-[15px] text-zinc-600 leading-relaxed" style={{ fontFamily: DM }}>{interviewData.aiSummary}</p>
                  </div>
                </div>
              )}

              {/* Skills + Communication */}
              {skillRatings.length > 0 && (
                <div className="flex flex-col gap-4">
                  <div className="bg-white border border-zinc-200">
                    <div className="px-6 py-3.5 border-b border-zinc-200">
                      <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400" style={{ fontFamily: DM }}>Skills Analysis</p>
                    </div>
                    <div className="px-6 py-5 flex gap-8">
                      <div style={{ width: "40%", minWidth: "40%", flexShrink: 0 }}>
                        <div style={{ height: 260 }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <RadarChart data={radarData} margin={{ top: 10, right: 30, bottom: 10, left: 30 }}>
                              <PolarGrid stroke="#e5e7eb" />
                              <PolarAngleAxis dataKey="subject" tick={{ fontSize: 11, fill: "#71717a", fontFamily: DM }} />
                              <Radar name="Score" dataKey="value" stroke="#18181b" fill="#18181b" fillOpacity={0.07} strokeWidth={1.5} />
                            </RadarChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="mt-3 pt-3 border-t border-zinc-100">
                          <div className="flex items-center justify-between mb-2">
                            <SectionLabel>Overall Match</SectionLabel>
                            <span className="text-sm font-bold" style={{ fontFamily: MONO, color: scoreColor(overallSkillsAvg) }}>
                              {overallSkillsAvg.toFixed(1)}/10
                            </span>
                          </div>
                          <div className="h-2 bg-zinc-100">
                            <div style={{ width: `${(overallSkillsAvg / 10) * 100}%`, background: scoreColor(overallSkillsAvg), height: "100%" }} />
                          </div>
                        </div>
                      </div>
                      <div className="w-px bg-zinc-100 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <SectionLabel>Skill Breakdown</SectionLabel>
                        <div className="space-y-4">
                          {skillRatings.map((s) => (
                            <div key={s.name_of_the_skill_tested}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-semibold text-zinc-700" style={{ fontFamily: DM }}>
                                  {s.name_of_the_skill_tested}
                                </span>
                                <span className="text-[11px] font-bold px-1.5 py-0.5" style={{ ...scoreBadgeStyle(s.rating), borderRadius: 0, fontFamily: MONO }}>
                                  {scoreLabel(s.rating)}
                                </span>
                              </div>
                              <ScoreBar score={s.rating} />
                              <p className="text-[15px] text-zinc-400 mt-1 leading-snug" style={{ fontFamily: DM }}>{s.reasoning}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Communication */}
                  {interviewData?.communication_skills && (
                    <div className="bg-white border border-zinc-200">
                      <div className="px-6 py-3.5 border-b border-zinc-200 flex items-center justify-between">
                        <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400" style={{ fontFamily: DM }}>Communication Skills</p>
                        <span className="text-[11px] font-bold px-1.5 py-0.5" style={{ ...scoreBadgeStyle(commScore), borderRadius: 0, fontFamily: MONO }}>
                          CEFR B1
                        </span>
                      </div>
                      <div className="px-6 py-5 flex gap-6">
                        <div className="shrink-0 flex flex-col items-center justify-center gap-3" style={{ width: 130 }}>
                          <div className="text-center">
                            <p className="text-5xl font-bold leading-none" style={{ fontFamily: MONO, color: scoreColor(commScore) }}>
                              {commScore}
                            </p>
                            <p className="text-[11px] text-zinc-400 mt-1" style={{ fontFamily: DM }}>/10 overall</p>
                          </div>
                          <div className="text-xs font-bold px-3 py-1.5 w-full text-center" style={{ ...scoreBadgeStyle(commScore), borderRadius: 0, fontFamily: DM }}>
                            {scoreLabel(commScore)}
                          </div>
                          <p className="text-[13px] text-zinc-400 text-center leading-snug mt-1" style={{ fontFamily: DM }}>
                            {interviewData.communication_skills.feedback}
                          </p>
                        </div>
                        <div className="w-px bg-zinc-100 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <SectionLabel>Dimension Scores</SectionLabel>
                          <div className="grid grid-cols-2 gap-2">
                            {Object.entries(interviewData.communication_skills.score_breakdown || {}).map(([k, v]) => {
                              const bs = scoreBadgeStyle(v)
                              return (
                                <div key={k} className="flex items-center justify-between px-3 py-2.5 border border-zinc-100 bg-zinc-50" style={{ borderRadius: 0 }}>
                                  <span className="text-[14px] text-zinc-600 leading-tight pr-2" style={{ fontFamily: DM }}>{k}</span>
                                  <span className="text-sm font-bold shrink-0" style={{ fontFamily: MONO, color: bs.color }}>{v}</span>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Strengths / Weaknesses fallback */}
              {!explanations.candidate_positives && (interviewData?.strengths?.length > 0 || interviewData?.weaknesses?.length > 0) && (
                <div className="bg-white border border-zinc-200">
                  <div className="px-6 py-3.5 border-b border-zinc-200">
                    <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400" style={{ fontFamily: DM }}>Overall Assessment</p>
                  </div>
                  <div className="px-6 py-5 grid grid-cols-2 gap-6">
                    {interviewData.strengths?.length > 0 && (
                      <div>
                        <SectionLabel>Strengths</SectionLabel>
                        <DotList items={interviewData.strengths} color="#34d399" />
                      </div>
                    )}
                    {interviewData.weaknesses?.length > 0 && (
                      <div>
                        <SectionLabel>Areas of Improvement</SectionLabel>
                        <DotList items={interviewData.weaknesses} color="#f87171" />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ════ RIGHT — Interviewer Decision + Transcript / Q-by-Q ════ */}
          <div className="flex flex-col bg-white shrink-0" style={{ width: "36%" }}>

            {/* Interviewer Decision Banner */}
            <div className="shrink-0 px-5 py-4 bg-white border-b border-zinc-200">
              <div className="border border-zinc-200 bg-zinc-50 px-5 py-4 flex items-center justify-between gap-4">
                <div className="flex flex-col gap-2">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400" style={{ fontFamily: DM }}>
                    Interviewer Decision
                  </p>
                  {hiringDecisionLabel ? (
                    <span className="text-[13px] font-bold px-3 py-1 self-start" style={{ borderRadius: 0, fontFamily: MONO, ...hiringDecisionStyle }}>
                      {hiringDecisionLabel}
                    </span>
                  ) : (
                    <p className="text-[13px] text-zinc-400" style={{ fontFamily: MONO }}>No decision recorded yet</p>
                  )}
                </div>
                <button
                  onClick={() => setShowFeedbackModal(true)}
                  className="flex items-center gap-1.5 h-9 bg-zinc-900 text-white text-[11px] font-bold uppercase tracking-widest px-4 hover:bg-zinc-700 transition-colors shrink-0"
                  style={{ borderRadius: 0, fontFamily: DM }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                  </svg>
                  {feedbackSaved ? "Edit Decision" : "Add Decision"}
                </button>
              </div>
            </div>

            {/* Right-panel tabs */}
            <div className="flex border-b border-zinc-200 shrink-0 bg-white">
              {[
                { key: "transcript", label: "Transcript" },
                { key: "feedback",   label: "Q by Q Feedback" },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`flex-1 px-4 py-3 text-[11px] font-bold uppercase tracking-widest border-r border-zinc-200 last:border-r-0 transition-all relative ${
                    activeTab === key ? "text-zinc-900 bg-white" : "text-zinc-400 bg-white hover:text-zinc-600 hover:bg-zinc-50"
                  }`}
                  style={{ borderRadius: 0, fontFamily: DM }}
                >
                  {label}
                  {activeTab === key && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-zinc-900" />
                  )}
                </button>
              ))}
            </div>

            {/* Scrollable panel content */}
            <div className="flex-1 overflow-y-auto hide-scroll">
              {activeTab === "transcript" ? (
                <div className="px-7 py-6">
                  {transcripts.length > 0 ? (
                    transcripts.map((msg, i) => (
                      <Bubble key={i} role={msg.sender === "interviewer" ? "assistant" : "user"} text={msg.text} />
                    ))
                  ) : qaPairs.length > 0 ? (
                    qaPairs.map((pair, i) => (
                      <div key={i}>
                        <Bubble role="assistant" text={pair.question} />
                        {pair.answer && <Bubble role="user" text={pair.answer} />}
                      </div>
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full py-20">
                      <FileText size={28} className="text-zinc-200 mb-3" />
                      <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-300" style={{ fontFamily: DM }}>
                        No transcript available
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="px-6 py-6 space-y-3">
                  {(explanations.question_by_question_feedback?.length > 0
                    ? explanations.question_by_question_feedback
                    : qaPairs
                  ).map((q, i) => {
                    const hasScore = "score" in q
                    return (
                      <div key={i} className="border border-zinc-200">
                        <div className="flex items-start justify-between gap-3 px-4 py-3.5 bg-zinc-50 border-b border-zinc-200">
                          <p className="text-[15px] font-semibold text-zinc-800 leading-snug flex-1" style={{ fontFamily: DM }}>
                            <span className="text-[11px] font-bold uppercase tracking-widest text-zinc-400 mr-2" style={{ fontFamily: MONO }}>
                              Q{i + 1}
                            </span>
                            {q.question}
                          </p>
                          {hasScore && (
                            <div className="shrink-0 flex flex-col items-end gap-1">
                              <span className="text-base font-bold" style={{ fontFamily: MONO, color: scoreColor(q.score) }}>
                                {q.score}/10
                              </span>
                              <Badge style={scoreBadgeStyle(q.score)}>{scoreLabel(q.score)}</Badge>
                            </div>
                          )}
                        </div>
                        <div className="px-4 py-3.5 border-b border-zinc-100">
                          <SectionLabel>Candidate's Answer</SectionLabel>
                          <p className="text-[14px] text-zinc-600 leading-relaxed bg-zinc-50 px-3 py-2.5 border border-zinc-100" style={{ fontFamily: MONO, borderRadius: 0 }}>
                            {q.answer || "No answer recorded"}
                          </p>
                        </div>
                        {q["analysis/feedback"] && (
                          <div className="px-4 py-3.5">
                            <SectionLabel>Feedback</SectionLabel>
                            <p className="text-[15px] text-zinc-600 leading-relaxed" style={{ fontFamily: DM }}>{q["analysis/feedback"]}</p>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

      ) : (
        /* ════ RECORDING TAB ════ */
        <div className="flex flex-1 min-h-0 bg-zinc-100 gap-px">

          {/* Integrity Analysis */}
          <div className="flex flex-col bg-zinc-50 overflow-y-auto hide-scroll" style={{ width: "50%" }}>
            <div className="bg-zinc-50 px-10 py-5 shrink-0">
              <div className="bg-white border border-zinc-200 px-6 py-4 flex items-center gap-3">
                <div className="w-6 h-6 flex items-center justify-center shrink-0" style={{ background: "#fffbeb", border: "1px solid #fde68a" }}>
                  <AlertCircle size={12} color="#b45309" />
                </div>
                <p className="text-xs font-bold uppercase tracking-widest text-zinc-900" style={{ fontFamily: DM }}>Integrity Analysis</p>
                <span className="text-[10px] font-bold px-1.5 py-0.5 uppercase tracking-widest" style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#dc2626", borderRadius: 0, fontFamily: MONO }}>
                  Needs Review
                </span>
              </div>
            </div>
            <div className="px-10 space-y-4 pb-8">
              <div className="bg-white border border-zinc-200">
                <div className="px-6 py-3.5 border-b border-zinc-200">
                  <p className="text-[11px] font-bold uppercase tracking-widest text-zinc-400" style={{ fontFamily: DM }}>Screen Analysis</p>
                </div>
                <div className="px-6 py-20 flex items-center justify-center">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 size={14} className="text-emerald-500" />
                    <p className="text-[14px] text-zinc-500" style={{ fontFamily: DM }}>Nothing Suspicious Detected</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Video Player */}
          <div className="flex flex-col bg-white overflow-y-auto hide-scroll" style={{ width: "50%" }}>
            <div className="bg-white px-10 py-5 shrink-0">
              <div className="bg-white border border-zinc-200 px-8 py-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <p className="text-sm font-bold text-zinc-900" style={{ fontFamily: DM }}>Interview Recording</p>
                  <span className="text-[10px] font-bold px-2 py-0.5 uppercase tracking-widest" style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", color: "#15803d", borderRadius: 0, fontFamily: MONO }}>
                    Done
                  </span>
                </div>
                <span className="text-[12px] text-zinc-400" style={{ fontFamily: MONO }}>
                  {date
                    ? new Date(date).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
                    : new Date().toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                </span>
              </div>
            </div>
            <VideoPlayer
              recordingUrl={interviewData.recordingUrl}
              duration={duration}
              flaggedTimestamps={(interviewData.transcriptAnalysis?.flagged_timestamps ?? []).map((f) => ({ ...f, source: "transcript" }))}
            />
          </div>
        </div>
      )}

      {/* ── Modals ── */}
      {showNotesViewer && (
        <NotesViewer
          isOpen={showNotesViewer}
          onClose={() => setShowNotesViewer(false)}
          meetingId={meetingId}
          userId={user?.uid}
          interviewTitle={interview?.title || interviewData?.title}
          interviewDate={interview?.date || interviewData?.date}
        />
      )}
      {showFeedbackModal && (
        <InterviewerFeedbackModal
          isOpen={showFeedbackModal}
          onClose={() => setShowFeedbackModal(false)}
          initialFeedback={interviewerFeedback}
          initialDecision={hiringDecision}
          onSave={handleSaveFeedback}
          isSaving={isSavingFeedback}
        />
      )}
    </div>
  )
}

export default InterviewDetailsPage